import { useEffect, useRef } from 'react';
import { createStore, useStore } from '../app/store';

/**
 * The toast and the tooltip (control-room-web.html: "Tooltips", "Actions"): one style for every icon-only control
 * and dotted word, and one line at the bottom for what just happened, with Undo while it can still be undone.
 */

interface ToastState {
  msg: string | null;
  undo: (() => string | null | void | Promise<string | null | void>) | null;
  /** A ring that empties over 5 seconds: what's held before it's sent. */
  ring: boolean;
  n: number;
}

export const toasts = createStore<ToastState>({ msg: null, undo: null, ring: false, n: 0 });
let toastTimer: ReturnType<typeof setTimeout> | undefined;

export function toast(msg: string, opts: { undo?: ToastState['undo']; ms?: number; ring?: boolean } = {}) {
  clearTimeout(toastTimer);
  toasts.set((s) => ({ msg, undo: opts.undo ?? null, ring: !!opts.ring, n: s.n + 1 }));
  toastTimer = setTimeout(() => toasts.set({ msg: null, undo: null, ring: false }), opts.ms ?? 4200);
}

/** A control that isn't wired: says so rather than doing nothing. */
export const later = (what = 'That') => toast(`${what} isn’t in Routr Dispatch yet`);

export function Toast() {
  const t = useStore(toasts);
  const ring = useRef<SVGCircleElement>(null);
  useEffect(() => {
    const c = ring.current;
    if (!c) return;
    c.style.transition = 'none';
    c.style.strokeDashoffset = '0';
    c.getBoundingClientRect();
    requestAnimationFrame(() => {
      c.style.transition = 'stroke-dashoffset 5s linear';
      c.style.strokeDashoffset = '37.7';
    });
  }, [t.n, t.ring]);
  if (!t.msg) return null;
  return (
    <div id="toast" className="on" role="status" aria-live="polite">
      <span id="toastmsg">{t.msg}</span>
      {t.undo && (
        <button
          id="toastundo"
          onClick={async () => {
            const undo = t.undo;
            toasts.set({ undo: null, msg: 'Undoing…', ring: false });
            const no = await undo?.();
            if (no) toast(no);
            else toast('Undone', { ms: 1600 });
          }}
        >
          {t.ring && (
            <svg className="ring" viewBox="0 0 16 16" aria-hidden="true">
              <circle ref={ring} cx="8" cy="8" r="6" />
            </svg>
          )}
          Undo
        </button>
      )}
    </div>
  );
}

/** Tooltips for anything with `data-tip`: the first waits half a second, its neighbours then show at once. */
export function Tip() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tip = ref.current!;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let current: HTMLElement | null = null;
    let last = 0;
    const hide = () => {
      if (tip.classList.contains('on')) last = performance.now();
      tip.classList.remove('on');
      current = null;
    };
    const show = (t: HTMLElement) => {
      if (!t.isConnected || document.querySelector('.menu') || document.body.classList.contains('dragging')) return;
      tip.textContent = t.dataset.tip ?? '';
      tip.classList.add('on');
      current = t;
      const r = t.getBoundingClientRect();
      const w = tip.offsetWidth;
      const h = tip.offsetHeight;
      const side = t.dataset.side;
      let x = r.left + r.width / 2 - w / 2;
      let y = r.bottom + 8;
      if (side === 'right') {
        x = r.right + 10;
        y = r.top + r.height / 2 - h / 2;
      } else if (side === 'left') {
        x = r.left - w - 10;
        y = r.top + r.height / 2 - h / 2;
      } else if (side === 'top' || y + h > innerHeight - 8) y = r.top - h - 8;
      tip.style.left = `${Math.max(8, Math.min(x, innerWidth - w - 8))}px`;
      tip.style.top = `${Math.max(8, y)}px`;
    };
    const over = (e: PointerEvent) => {
      const t = (e.target as HTMLElement).closest?.('[data-tip]') as HTMLElement | null;
      if (t === current) return;
      clearTimeout(timer);
      if (current) hide();
      if (!t) return;
      timer = setTimeout(() => show(t), performance.now() - last < 700 ? 0 : 500);
    };
    const down = () => {
      clearTimeout(timer);
      hide();
    };
    document.addEventListener('pointerover', over, true);
    document.addEventListener('pointerdown', down, true);
    const tidy = setInterval(() => current && !current.isConnected && hide(), 500);
    return () => {
      document.removeEventListener('pointerover', over, true);
      document.removeEventListener('pointerdown', down, true);
      clearInterval(tidy);
    };
  }, []);
  return <div id="tip" role="tooltip" ref={ref} />;
}
